import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Empty, Flex, Spin, Button, Alert } from "antd";
import { useQuery } from "@tanstack/react-query";
import DisplayCard from "../components/DisplayCard";
import { PopupBanner } from "../components/utils";

function CourseCategoryPage({
  queryKey,
  fetchFn,
  popupStorageKey,
  errorMessage,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: [queryKey],
    queryFn: fetchFn,
  });

  const [activeTab, setActiveTab] = useState("");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!data?.list?.length || initialized) return;

    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("tab");

    if (tabFromUrl) {
      const found = data.list.some(
        (c) => c.category_name.toLowerCase() === tabFromUrl.toLowerCase()
      );
      if (found) {
        setActiveTab(tabFromUrl);
        setInitialized(true);
        return;
      }
    }

    const first = data.list[0].category_name;
    setActiveTab(first);

    params.set("tab", first);
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );

    setInitialized(true);
  }, [data, initialized, location.pathname, location.search, navigate]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);

    const params = new URLSearchParams(location.search);
    params.set("tab", tab);

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true }
    );
  };

  const getCategoryObject = (name) => {
    if (!data || !Array.isArray(data.list)) return null;
    const lower = (n) => (n || "").toString().toLowerCase();
    return (
      data.list.find((c) => lower(c.category_name) === lower(name)) || null
    );
  };

  if (isLoading) {
    return (
      <div style={{ padding: 16, minHeight: "90vh", paddingTop: "45vh" }}>
        <Row justify="center">
          <Spin size="large" />
        </Row>
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 16, minHeight: "90vh", paddingTop: "20vh" }}>
        <Row justify="center">
          <Col>
            <Alert
              type="error"
              message={errorMessage}
              description="Please try again after some time."
            />
          </Col>
        </Row>
      </div>
    );
  }

  const catObj = getCategoryObject(activeTab);

  return (
    <div style={{ padding: 16, minHeight: "90vh" }}>
      <Row justify="center" align="middle">
        <Flex vertical style={{ marginBottom: 16, marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              borderRadius: 999,
              background: "#f0ededff",
            }}
          >
            {data?.list?.map((cat) => {
              const isActive = activeTab === cat.category_name;
              return (
                <Button
                  key={cat.category_name}
                  type="text"
                  onClick={() => handleTabClick(cat.category_name)}
                  style={{
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 400,
                    background: isActive ? "#ffffff" : "transparent",
                    boxShadow: isActive ? "0 0 0 1px #3facffff" : "none",
                  }}
                >
                  {cat.category_name}
                </Button>
              );
            })}
          </div>
        </Flex>
      </Row>

      <div style={{ marginTop: 20 }}>
        {!catObj || !catObj.list?.length ? (
          <div style={{ padding: 40 }}>
            <Empty description="No items available" />
          </div>
        ) : (
          <Row gutter={[16, 16]} justify="center">
            {catObj.list
              .filter((item) => item.enabled === true)
              .map((item) => (
              <Col xs={24} sm={12} md={8} lg={6} key={item.SKU}>
                <DisplayCard item={item} />
              </Col>
            ))}
          </Row>
        )}
      </div>

      <PopupBanner popup={data?.banner} POPUP_STORAGE_KEY={popupStorageKey} />
    </div>
  );
}

export default CourseCategoryPage;
